import React from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import PageHero from '../../components/ui/page-hero';
import { useTranslation } from '../../contexts/TranslationContext';
import { 
  Code, 
  Database, 
  Cloud, 
  Smartphone, 
  Brain, 
  ArrowRight,
  Award,
  TrendingUp,
  Rocket,
  BarChart3,
  Settings,
  Palette,
  Search,
  ExternalLink
} from 'lucide-react';

// Brand icons from react-icons
import { 
  SiReact,
  SiNextdotjs,
  SiTypescript,
  SiTailwindcss,
  SiVuedotjs,
  SiSvelte,
  SiNodedotjs,
  SiPython,
  SiPostgresql,
  SiMongodb,
  SiRedis,
  SiGraphql,
  SiAmazonwebservices,
  SiDocker,
  SiKubernetes,
  SiTerraform,
  SiGithubactions,
  SiPrometheus,
  SiOpenai,
  SiTensorflow,
  SiPytorch,
  SiLangchain,
  SiOpencv
} from 'react-icons/si';

const Technology = () => {
  const { t } = useTranslation();

  // Tech Stack Data
  const techStack = {
    frontend: [
      { name: 'React', icon: SiReact, description: 'Modern UI library', level: 95 },
      { name: 'Next.js', icon: SiNextdotjs, description: 'Full-stack framework', level: 90 },
      { name: 'TypeScript', icon: SiTypescript, description: 'Type-safe JavaScript', level: 88 },
      { name: 'Tailwind CSS', icon: SiTailwindcss, description: 'Utility-first CSS', level: 92 },
      { name: 'Vue.js', icon: SiVuedotjs, description: 'Progressive framework', level: 85 },
      { name: 'Svelte', icon: SiSvelte, description: 'Compile-time framework', level: 80 }
    ],
    backend: [
      { name: 'Node.js', icon: SiNodedotjs, description: 'JavaScript runtime', level: 95 },
      { name: 'Python', icon: SiPython, description: 'Versatile programming', level: 90 },
      { name: 'PostgreSQL', icon: SiPostgresql, description: 'Advanced database', level: 88 },
      { name: 'MongoDB', icon: SiMongodb, description: 'NoSQL database', level: 85 },
      { name: 'Redis', icon: SiRedis, description: 'In-memory store', level: 82 },
      { name: 'GraphQL', icon: SiGraphql, description: 'Query language', level: 80 }
    ],
    cloud: [
      { name: 'AWS', icon: SiAmazonwebservices, description: 'Amazon Web Services', level: 90 },
      { name: 'Docker', icon: SiDocker, description: 'Containerization', level: 88 },
      { name: 'Kubernetes', icon: SiKubernetes, description: 'Container orchestration', level: 85 },
      { name: 'Terraform', icon: SiTerraform, description: 'Infrastructure as code', level: 82 },
      { name: 'CI/CD', icon: SiGithubactions, description: 'Continuous integration', level: 90 },
      { name: 'Monitoring', icon: SiPrometheus, description: 'System monitoring', level: 85 }
    ],
    ai: [
      { name: 'OpenAI GPT', icon: SiOpenai, description: 'Large language models', level: 90 },
      { name: 'TensorFlow', icon: SiTensorflow, description: 'Machine learning', level: 85 },
      { name: 'PyTorch', icon: SiPytorch, description: 'Deep learning', level: 80 },
      { name: 'LangChain', icon: SiLangchain, description: 'LLM applications', level: 88 },
      { name: 'Vector DBs', icon: Database, description: 'Embedding storage', level: 85 },
      { name: 'Computer Vision', icon: SiOpencv, description: 'Image processing', level: 80 }
    ]
  };

  // Expertise Areas
  const expertiseAreas = [
    {
      title: t('technology.expertise.fullStack.title'),
      description: t('technology.expertise.fullStack.description'),
      icon: Code,
      technologies: ['React', 'Node.js', 'PostgreSQL', 'AWS'],
      projects: 50,
      experience: t('technology.expertise.fullStack.experience')
    },
    {
      title: t('technology.expertise.ai.title'),
      description: t('technology.expertise.ai.description'),
      icon: Brain,
      technologies: ['OpenAI', 'TensorFlow', 'LangChain', 'Vector DBs'],
      projects: 25,
      experience: t('technology.expertise.ai.experience')
    },
    {
      title: t('technology.expertise.cloud.title'),
      description: t('technology.expertise.cloud.description'),
      icon: Cloud,
      technologies: ['AWS', 'Docker', 'Kubernetes', 'Terraform'],
      projects: 40,
      experience: t('technology.expertise.cloud.experience')
    },
    {
      title: t('technology.expertise.devops.title'),
      description: t('technology.expertise.devops.description'),
      icon: Settings,
      technologies: ['GitHub Actions', 'Docker', 'Kubernetes', 'Monitoring'],
      projects: 35,
      experience: t('technology.expertise.devops.experience')
    },
    {
      title: t('technology.expertise.mobile.title'),
      description: t('technology.expertise.mobile.description'),
      icon: Smartphone,
      technologies: ['React Native', 'Flutter', 'iOS', 'Android'],
      projects: 20,
      experience: t('technology.expertise.mobile.experience')
    },
    {
      title: t('technology.expertise.data.title'),
      description: t('technology.expertise.data.description'),
      icon: BarChart3,
      technologies: ['Python', 'PostgreSQL', 'Redis', 'Analytics'],
      projects: 15,
      experience: t('technology.expertise.data.experience')
    }
  ];

  // Development Methodology
  const methodologySteps = [
    {
      step: '01',
      title: t('technology.methodology.discovery.title'),
      description: t('technology.methodology.discovery.description'),
      icon: Search,
      duration: t('technology.methodology.discovery.duration'),
      deliverables: t('technology.methodology.discovery.deliverables')
    },
    {
      step: '02',
      title: t('technology.methodology.design.title'),
      description: t('technology.methodology.design.description'),
      icon: Palette,
      duration: t('technology.methodology.design.duration'),
      deliverables: t('technology.methodology.design.deliverables')
    },
    {
      step: '03',
      title: t('technology.methodology.development.title'),
      description: t('technology.methodology.development.description'),
      icon: Code,
      duration: t('technology.methodology.development.duration'),
      deliverables: t('technology.methodology.development.deliverables')
    },
    {
      step: '04',
      title: t('technology.methodology.deployment.title'),
      description: t('technology.methodology.deployment.description'),
      icon: Rocket,
      duration: t('technology.methodology.deployment.duration'),
      deliverables: t('technology.methodology.deployment.deliverables')
    },
    {
      step: '05',
      title: t('technology.methodology.maintenance.title'),
      description: t('technology.methodology.maintenance.description'),
      icon: TrendingUp,
      duration: t('technology.methodology.maintenance.duration'),
      deliverables: t('technology.methodology.maintenance.deliverables')
    }
  ];

  // Case Studies
  const caseStudies = [
    {
      title: t('technology.caseStudies.ecommerce.title'),
      description: t('technology.caseStudies.ecommerce.description'),
      // image: 'https://api.dicebear.com/7.x/initials/svg?seed=Ecommerce', // Removed external API dependency
      technologies: ['React', 'Node.js', 'PostgreSQL', 'AWS'],
      metrics: { 
        users: t('technology.caseStudies.ecommerce.metrics.users'), 
        performance: t('technology.caseStudies.ecommerce.metrics.performance'), 
        revenue: t('technology.caseStudies.ecommerce.metrics.revenue') 
      },
      color: 'from-blue-500 to-purple-500'
    },
    {
      title: t('technology.caseStudies.analytics.title'),
      description: t('technology.caseStudies.analytics.description'),
      // image: 'https://api.dicebear.com/7.x/initials/svg?seed=Analytics', // Removed external API dependency
      technologies: ['Python', 'TensorFlow', 'React', 'AWS'],
      metrics: { 
        accuracy: t('technology.caseStudies.analytics.metrics.accuracy'), 
        insights: t('technology.caseStudies.analytics.metrics.insights'), 
        efficiency: t('technology.caseStudies.analytics.metrics.efficiency') 
      },
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: t('technology.caseStudies.banking.title'),
      description: t('technology.caseStudies.banking.description'),
      // image: 'https://api.dicebear.com/7.x/initials/svg?seed=Banking', // Removed external API dependency
      technologies: ['React Native', 'Node.js', 'PostgreSQL', 'AWS'],
      metrics: { 
        downloads: t('technology.caseStudies.banking.metrics.downloads'), 
        security: t('technology.caseStudies.banking.metrics.security'), 
        rating: t('technology.caseStudies.banking.metrics.rating') 
      },
      color: 'from-green-500 to-teal-500'
    }
  ];

  // Team Expertise
  const teamMembers = [
    {
      name: t('technology.team.ahmed.name'),
      role: t('technology.team.ahmed.role'),
      expertise: ['React', 'Node.js', 'AWS', 'PostgreSQL'],
      experience: t('technology.team.ahmed.experience'),
      certifications: t('technology.team.ahmed.certifications'),
      // image: 'https://api.dicebear.com/7.x/initials/svg?seed=Ahmed' // Removed external API dependency
    },
    {
      name: t('technology.team.fatima.name'),
      role: t('technology.team.fatima.role'),
      expertise: ['Python', 'TensorFlow', 'OpenAI', 'LangChain'],
      experience: t('technology.team.fatima.experience'),
      certifications: t('technology.team.fatima.certifications'),
      // image: 'https://api.dicebear.com/7.x/initials/svg?seed=Fatima' // Removed external API dependency
    },
    {
      name: t('technology.team.youssef.name'),
      role: t('technology.team.youssef.role'),
      expertise: ['Docker', 'Kubernetes', 'AWS', 'Terraform'],
      experience: t('technology.team.youssef.experience'),
      certifications: t('technology.team.youssef.certifications'),
      // image: 'https://api.dicebear.com/7.x/initials/svg?seed=Youssef' // Removed external API dependency
    },
    {
      name: t('technology.team.aicha.name'),
      role: t('technology.team.aicha.role'),
      expertise: ['Figma', 'React', 'Design Systems', 'Prototyping'],
      experience: t('technology.team.aicha.experience'),
      certifications: t('technology.team.aicha.certifications'),
      // image: 'https://api.dicebear.com/7.x/initials/svg?seed=Aicha' // Removed external API dependency
    }
  ];

  // Certifications
  const certifications = [
    { name: t('technology.certifications.aws.name'), issuer: t('technology.certifications.aws.issuer'), year: t('technology.certifications.aws.year'), icon: Award },
    { name: t('technology.certifications.gcp.name'), issuer: t('technology.certifications.gcp.issuer'), year: t('technology.certifications.gcp.year'), icon: Award },
    { name: t('technology.certifications.react.name'), issuer: t('technology.certifications.react.issuer'), year: t('technology.certifications.react.year'), icon: Award },
    { name: t('technology.certifications.tensorflow.name'), issuer: t('technology.certifications.tensorflow.issuer'), year: t('technology.certifications.tensorflow.year'), icon: Award },
    { name: t('technology.certifications.kubernetes.name'), issuer: t('technology.certifications.kubernetes.issuer'), year: t('technology.certifications.kubernetes.year'), icon: Award },
    { name: t('technology.certifications.scrum.name'), issuer: t('technology.certifications.scrum.issuer'), year: t('technology.certifications.scrum.year'), icon: Award }
  ];

  return (
    <>
      <Helmet>
        <title>Technology Stack & Tools | Envael Morocco</title>
        <meta name="description" content="Discover the cutting-edge technologies and tools Envael uses for digital transformation in Morocco. From AI and machine learning to modern web frameworks and cloud solutions." />
        <meta name="keywords" content="technology stack morocco, AI tools, web development technologies, cloud computing, machine learning, React development, Node.js, digital transformation tools" />
        <meta property="og:title" content="Technology Stack & Tools | Envael Morocco" />
        <meta property="og:description" content="Discover the cutting-edge technologies and tools Envael uses for digital transformation in Morocco. From AI to modern web frameworks." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://envael.com/technology" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Technology Stack & Tools | Envael Morocco" />
        <meta name="twitter:description" content="Discover the cutting-edge technologies and tools Envael uses for digital transformation in Morocco." />
        <link rel="canonical" href="https://envael.com/technology" />
      </Helmet>

      <div className="min-h-screen bg-background">
      {/* Page Hero with Navigation */}
      <PageHero 
        pageName={t('technology.hero.title')} 
        subtitle={t('technology.hero.subtitle')}
        size="large"
        fullHeight={true}
        enableSpline={true}
      />

      {/* Tech Stats Section */}
      <section className="py-16 bg-gradient-to-br from-background via-muted/20 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">50+</div>
              <div className="text-muted-foreground">{t('technology.stats.technologies')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">100+</div>
              <div className="text-muted-foreground">{t('technology.stats.projects')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">5+</div>
              <div className="text-muted-foreground">{t('technology.stats.experience')}</div>
            </div>
        <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">99.9%</div>
              <div className="text-muted-foreground">{t('technology.stats.uptime')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack Section */}
      <section id="tech-stack" className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              {t('technology.techStack.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('technology.techStack.subtitle')}
            </p>
          </div>

          {Object.entries(techStack).map(([category, technologies]) => (
            <div key={category} className="mb-16">
              <h3 className="text-2xl font-bold text-foreground mb-8 capitalize">
                {t(`technology.techStack.${category}`)}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {technologies.map((tech, index) => (
                  <Card key={tech.name} className="group hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/30">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="p-3 rounded-lg bg-primary/10">
                          {React.createElement(tech.icon, { className: "h-6 w-6 text-primary" })}
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-muted-foreground">{tech.level}%</div>
                          <div className="w-16 bg-muted rounded-full h-2">
                            <div 
                              className="bg-primary h-2 rounded-full transition-all duration-1000"
                              style={{ width: `${tech.level}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      <h4 className="text-lg font-semibold text-foreground mb-2">{tech.name}</h4>
                      <p className="text-sm text-muted-foreground">{tech.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Expertise Section */}
      <section id="expertise" className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              {t('technology.expertise.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('technology.expertise.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {expertiseAreas.map((area, index) => (
              <Card key={area.title} className="group hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/30 overflow-hidden">
                <CardContent className="p-0">
                  <div className="bg-primary p-6 text-primary-foreground">
                    <div className="flex items-center justify-between mb-4">
                      {React.createElement(area.icon, { className: "h-8 w-8" })}
                      <div className="text-right">
                        <div className="text-sm opacity-90">{area.experience}</div>
                        <div className="text-xs opacity-75">{area.projects} projects</div>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold mb-2">{area.title}</h3>
                    <p className="text-sm opacity-90 leading-relaxed">{area.description}</p>
                  </div>
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2">
                      {area.technologies.map((tech) => (
                        <span key={tech} className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section id="methodology" className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              {t('technology.methodology.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('technology.methodology.subtitle')}
            </p>
          </div>

          <div className="space-y-8">
            {methodologySteps.map((step, index) => (
              <div key={step.step} className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {step.step}
                  </div>
                </div>
                <div className="flex-1">
                  <Card className="hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-8">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-primary/10 rounded-lg">
                            {React.createElement(step.icon, { className: "h-6 w-6 text-primary" })}
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold text-foreground">{step.title}</h3>
                            <p className="text-muted-foreground">{step.duration}</p>
                          </div>
                        </div>
                      </div>
                      <p className="text-muted-foreground mb-6 leading-relaxed">{step.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {(Array.isArray(step.deliverables) ? step.deliverables : []).map((deliverable) => (
                          <span key={deliverable} className="px-3 py-1 bg-muted text-muted-foreground text-sm rounded-full">
                            {deliverable}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              {t('technology.caseStudies.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('technology.caseStudies.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <Card key={study.title} className="group hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/30 overflow-hidden">
                <CardContent className="p-0">
                  <div className={`bg-gradient-to-r ${study.color} p-6 text-white`}>
                    <div className="flex items-center justify-between mb-4">
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={study.image} />
                        <AvatarFallback>{study.title.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <ExternalLink className="h-5 w-5 opacity-75" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{study.title}</h3>
                    <p className="text-sm opacity-90 leading-relaxed">{study.description}</p>
                  </div>
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 mb-6">
                      {study.technologies.map((tech) => (
                        <span key={tech} className="px-3 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                          {tech}
                        </span>
                      ))}
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      {Object.entries(study.metrics).map(([key, value]) => (
                        <div key={key}>
                          <div className="text-lg font-bold text-primary">{value}</div>
                          <div className="text-xs text-muted-foreground capitalize">{key}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              {t('technology.team.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('technology.team.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <Card key={member.name} className="group hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/30">
                <CardContent className="p-6 text-center">
                  <Avatar className="w-20 h-20 mx-auto mb-4">
                    <AvatarImage src={member.image} />
                    <AvatarFallback>{member.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                  </Avatar>
                  <h3 className="text-lg font-bold text-foreground mb-1">{member.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{member.role}</p>
                  <div className="flex flex-wrap gap-1 justify-center mb-4">
                    {member.expertise.map((skill) => (
                      <span key={skill} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                        {skill}
                      </span>
                    ))}
                  </div>
                  <div className="text-xs text-muted-foreground mb-2">{member.experience}</div>
                  <div className="flex flex-wrap gap-1 justify-center">
                    {(Array.isArray(member.certifications) ? member.certifications : []).map((cert) => (
                      <span key={cert} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                        {cert}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section id="certifications" className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              {t('technology.certifications.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('technology.certifications.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <Card key={cert.name} className="group hover:shadow-lg transition-all duration-300 border-border/50 hover:border-primary/30">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      {React.createElement(cert.icon, { className: "h-6 w-6 text-primary" })}
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{cert.name}</h3>
                      <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">Issued: {cert.year}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            {t('technology.cta.title')}
          </h2>
          <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
            {t('technology.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="text-lg px-8 py-6">
              {t('technology.cta.primary')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button variant="outline" size="lg" className="text-lg px-8 py-6">
              {t('technology.cta.secondary')}
            </Button>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Technology;
